export type RoomPhase =
  | "LOBBY"
  | "COUNTDOWN"
  | "SECRET_REVEAL"
  | "DISCUSSION"
  | "VOTING"
  | "RESULTS"

export type GameMode = "classic" | "timed" | "multi-imposter" | "extended"
export type TopicDifficulty = "EASY" | "MEDIUM" | "HARD"

/**
 * Topic payload as the engine consumes it. Produced by the Worker via a DB
 * draw (selectTopicForRound) and either passed directly into the START_ROUND
 * action or returned synchronously by the EngineDeps.pickTopic fallback.
 */
export type PickedTopic = {
  topicId: string
  title: string
  reference?: string | null
  aliases: string[]
  difficulty: TopicDifficulty
  /** True when the topic pool was exhausted and reset before this pick. */
  didReset?: boolean
}

/**
 * Topic categories the host may include in the pool from the lobby. Mirrors
 * the `category` column on `topics`. Empty array on RoomState is the storage
 * default for legacy/migrated rooms and means "no filter" (all categories);
 * the lobby UI guarantees a non-empty user-chosen subset so empty never
 * surfaces from new rooms.
 */
export type TopicCategory =
  | "CHARACTERS"
  | "EVENTS"
  | "OBJECTS"
  | "PARABLES"
  | "VERSES"
  | "PLACES"

export const ALL_TOPIC_CATEGORIES: readonly TopicCategory[] = [
  "CHARACTERS",
  "EVENTS",
  "OBJECTS",
  "PARABLES",
  "VERSES",
  "PLACES",
] as const

/**
 * Number of imposters per round, chosen by the host at room creation.
 * Bounded 1..3; rounds with too few active players for the chosen count
 * fall back to the largest feasible number (always >= 1).
 */
export type ImposterCount = 1 | 2 | 3

export type CreateRoomRequest = {
  name: string
  deviceToken: string
  mode: GameMode
  imposterCount: ImposterCount
}

export type Player = {
  playerId: string
  name: string
  normalizedName: string
  deviceToken: string
  sessionTokenHash: string
  isHost: boolean
  joinedAt: number
  disconnectedAt: number | null
}

export type Spectator = {
  playerId: string
  name: string
  normalizedName: string
  deviceToken: string
  spectatorTokenHash: string
  joinedAt: number
  disconnectedAt: number | null
}

export type RoundOutcome = "TEAM_WIN" | "IMPOSTER_WIN" | "CANCELLED"

/**
 * Match format selected at round start based on player count.
 *
 * The phone is a card deck + secret ballot box. Clue exchange happens out
 * loud in the physical room, not through the device — the engine never sees
 * clue text. A sub-round is just "discussion → vote".
 *
 * - "small" (3-4): one discussion, one sudden-death vote. No eliminations
 *   (too few players for an elimination ladder).
 * - "standard" (5+): each sub-round ends in a vote that may eliminate one
 *   player; round continues with survivors. Ends on team-win (imposter
 *   eliminated), imposter-win (tie vote = no-consensus = imposters get
 *   away, or survivors collapse to <=2), or until resolved.
 */
export type RoundVariant = "standard" | "small"

export type Round = {
  roundNumber: number
  variant: RoundVariant
  /** 1-indexed; increments after each non-eliminating advance in standard variant. */
  subRound: number
  topicId: string
  topicTitle: string
  topicReference: string | null
  topicAliases: string[]
  topicDifficulty: TopicDifficulty
  imposterIds: string[]
  countdownResets: number
  /** Votes for the CURRENT sub-round (cleared after each elimination in standard). */
  votes: Record<string, string>
  /** Players eliminated this round; cannot vote. Standard variant only. */
  eliminatedPlayerIds: string[]
  startedAt: number
  countdownEndsAt: number | null
  voteDeadline: number | null
  outcome: RoundOutcome | null
  accusedIds: string[]
  tally: Record<string, number>
  endedAt: number | null
  activePlayerIds: string[]
}

export type RoundSummary = {
  roundNumber: number
  topicId: string
  outcome: RoundOutcome
  durationMs: number
  imposterIds: string[]
}

export type RoomStateV1 = {
  schemaVersion: 1
  roomCode: string
  mode: GameMode
  imposterCount: ImposterCount
  /**
   * Host-curated subset of topic categories the round draw filters on. Empty
   * array means "all categories" — kept only for migration/legacy rooms;
   * the lobby UI never persists an empty selection.
   */
  categoryFilter: TopicCategory[]
  phase: RoomPhase
  createdAt: number
  updatedAt: number
  ttlExpiresAt?: number
  players: Player[]
  spectators: Spectator[]
  usedTopicIds: string[]
  currentRound: Round | null
  history: RoundSummary[]
}

export type RoomState = RoomStateV1

export type GameAction =
  | { type: "JOIN"; playerId: string; name: string; deviceToken: string; sessionTokenHash: string }
  | {
      type: "SPECTATE"
      playerId: string
      name: string
      deviceToken: string
      spectatorTokenHash: string
    }
  | { type: "LEAVE_ROOM"; playerId: string }
  | { type: "KICK_PLAYER"; hostId: string; targetId: string }
  | { type: "PROMOTE_SPECTATOR"; hostId: string; targetId: string }
  | { type: "UPDATE_CATEGORY_FILTER"; hostId: string; categories: TopicCategory[] }
  | {
      type: "START_ROUND"
      hostId: string
      /**
       * Pre-drawn topic from the Worker (DB query happens before the
       * reducer runs, then the result rides the action so the engine can
       * stash it on currentRound *immediately*. This makes the topic survive
       * DO hibernation between START_ROUND and the COUNTDOWN alarm —
       * otherwise an in-memory cache field gets wiped and pickTopic falls
       * back to a hardcoded seed.) Optional for backwards compatibility
       * and for tests that exercise the legacy pickTopic dep path.
       */
      topic?: PickedTopic
    }
  | { type: "CANCEL_COUNTDOWN"; hostId: string }
  | { type: "COUNTDOWN_ELAPSED" }
  | { type: "BEGIN_DISCUSSION"; hostId: string }
  | { type: "OPEN_VOTE"; playerId: string }
  | { type: "SUBMIT_VOTE"; voterId: string; suspectId: string }
  | { type: "VOTE_DEADLINE_ELAPSED" }
  | { type: "PLAYER_DISCONNECTED"; playerId: string }
  | { type: "RECONNECT_WINDOW_ELAPSED"; playerId: string }

export type AlarmKind = "TTL" | "COUNTDOWN" | "VOTE_DEADLINE" | "RECONNECT_WINDOW"

export type Effect =
  | { type: "BROADCAST_STATE" }
  | { type: "ACTION_REJECTED"; reason: string }
  | { type: "SEND_TO_PLAYER"; playerId: string; message: ServerMessage }
  | { type: "CLOSE_SOCKET"; playerId: string; reason: string }
  | { type: "UPDATE_SOCKET_ATTACHMENT"; playerId: string; attachment: { kind: "player"; playerId: string } }
  | { type: "ISSUE_NEW_SESSION_TOKEN"; playerId: string }
  | { type: "SET_ALARM_AT"; timestamp: number; kind: AlarmKind }
  | { type: "CLEAR_ALARM"; kind: AlarmKind }
  | { type: "EMIT_ANALYTICS"; event: AnalyticsEvent }
  | {
      type: "WRITE_ROUND_SCORES"
      roundNumber: number
      outcome: Exclude<RoundOutcome, "CANCELLED">
      imposterDeviceTokens: string[]
      teamDeviceTokens: string[]
    }
  | { type: "PERSIST_STATE" }

export type AnalyticsEvent = {
  type: string
  roomCode: string
  timestamp: number
  properties?: Record<string, string | number | boolean | null>
}

export type PublicPlayer = Pick<Player, "playerId" | "name" | "isHost" | "joinedAt"> & {
  isConnected: boolean
  hasVoted?: boolean
}

export type PublicSpectator = Pick<Spectator, "playerId" | "name" | "joinedAt"> & {
  isConnected: boolean
}

export type PublicRoundView = {
  roundNumber: number
  variant: RoundVariant
  subRound: number
  topicId: string | null
  votes: Record<string, string>
  eliminatedPlayerIds: string[]
  countdownEndsAt: number | null
  voteDeadline: number | null
  outcome: RoundOutcome | null
  accusedIds: string[]
  tally: Record<string, number>
  activePlayerIds: string[]
  topicTitle: string | null
  topicReference: string | null
  topicAliases: string[]
  imposterIds: string[]
}

export type PlayerView = {
  schemaVersion: 1
  roomCode: string
  mode: GameMode
  imposterCount: ImposterCount
  phase: RoomPhase
  currentRoundNumber: number
  /**
   * Host-only. Populated only when `you.isHost === true`. Imposters and team
   * players never receive this field — it could leak the topic pool.
   */
  categoryFilter?: TopicCategory[]
  you: {
    playerId: string
    name: string
    isHost: boolean
    isImposter: boolean | null
    myVote?: string
  }
  players: PublicPlayer[]
  spectators: PublicSpectator[]
  round: PublicRoundView | null
  history: RoundSummary[]
}

export type SpectatorView = Omit<PlayerView, "you" | "categoryFilter"> & {
  you: { playerId: string; name: string; isSpectator: true }
}

export type LobbyRoomView = {
  schemaVersion: RoomState["schemaVersion"]
  roomCode: string
  mode: RoomState["mode"]
  imposterCount: RoomState["imposterCount"]
  phase: RoomState["phase"]
  hostId: string | null
  players: Pick<PublicPlayer, "playerId" | "name" | "joinedAt" | "isHost">[]
  spectators: Pick<PublicSpectator, "playerId" | "name" | "joinedAt">[]
}

export type ClientMessage =
  | { type: "AUTH"; playerId: string; sessionToken: string }
  | { type: "JOIN"; name: string; deviceToken: string }
  | { type: "SPECTATE"; name: string; deviceToken: string }
  | AuthenticatedClientAction

export type AuthenticatedClientAction =
  | ({ type: "START_ROUND" } & PlayerActionAuth)
  | ({ type: "CANCEL_COUNTDOWN" } & PlayerActionAuth)
  | ({ type: "BEGIN_DISCUSSION" } & PlayerActionAuth)
  | ({ type: "OPEN_VOTE" } & PlayerActionAuth)
  | ({ type: "SUBMIT_VOTE"; suspectId: string } & PlayerActionAuth)
  | ({ type: "KICK_PLAYER"; targetId: string } & PlayerActionAuth)
  | ({ type: "PROMOTE_SPECTATOR"; targetId: string } & PlayerActionAuth)
  | ({ type: "UPDATE_CATEGORY_FILTER"; categories: TopicCategory[] } & PlayerActionAuth)
  | ({ type: "LEAVE_ROOM" } & PlayerActionAuth)

export type PlayerActionAuth = {
  playerId: string
  sessionToken: string
}

export type ServerMessage =
  | { type: "STATE"; payload: PlayerView | SpectatorView | LobbyRoomView }
  | { type: "AUTH_OK" }
  | { type: "AUTH_FAILED"; reason: string }
  | { type: "JOIN_OK"; playerId: string; sessionToken: string }
  | { type: "SPECTATE_OK"; playerId: string; spectatorToken: string }
  | { type: "ACTION_ACK"; action: ClientMessage["type"] }
  | { type: "ACTION_REJECTED"; reason: string }
  | { type: "KICKED"; reason: string }
  | { type: "HOST_PROMOTED"; playerId: string }
  | { type: "SESSION_TOKEN_ISSUED"; playerId: string; sessionToken: string }
  | { type: "SESSION_SUPERSEDED" }
  | { type: "ROOM_EXPIRED" }
  | { type: "ERROR"; message: string }
  | { type: "AUTH_REQUIRED" }
